import React, { useState, useEffect } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { isValidPhoneNumber } from "libphonenumber-js";
import { useNotificationStore } from "@/zustand/useNotificationStore";
import { useProfleUpdate } from "@/hooks/useProfile";
import { useGetCountries } from "@/hooks/useGetCountries";
import { useGetStates } from "@/hooks/useGetStates";
import ToastNotification from "@shared/ToastNotification";
import { COUNTRY_CODE_MAP } from "@/constants";
import { TeelogixLogo } from "./TeelogixLogo";
import { ChevronDown, ArrowRight } from "lucide-react";
import PhoneInput from "@/FormComponents/PhoneInput";

interface CompleteProfileFormData {
  first_name: string;
  last_name: string;
  phone: string;
  country?: string;
  state?: string;
  city?: string;
  zip_code?: string;
  address?: string;
  address_two?: string;
}

const completeProfileSchema = yup.object().shape({
  first_name: yup.string().required("First name is required"),
  last_name: yup.string().required("Last name is required"),
  phone: yup
    .string()
    .required("Phone number is required")
    .test(
      "is-valid-phone",
      "Please enter a valid phone number for the selected country",
      (value) => {
        if (!value) return false;
        try {
          return isValidPhoneNumber(value);
        } catch (error) {
          return false;
        }
      }
    ),
  country: yup.string(),
  state: yup.string(),
  city: yup.string(),
  zip_code: yup.string(),
  address: yup.string(),
  address_two: yup.string(),
});

interface CompleteProfileModalProps {
  isOpen: boolean;
  currentProfile: {
    first_name?: string;
    last_name?: string;
    phone?: string;
    email: string;
    country?: string;
    state?: string;
    city?: string;
    zip_code?: string;
    address?: string;
    address_two?: string;
  };
  onSuccess: () => void;
  onSignOut?: () => void;
}

const CompleteProfileModal: React.FC<CompleteProfileModalProps> = ({
  isOpen,
  currentProfile,
  onSuccess,
  onSignOut,
}) => {
  const { setNotification } = useNotificationStore();
  const { mutate: updateProfile, isPending } = useProfleUpdate();
  const [countryCode, setCountryCode] = useState<string | null>(null);

  const { data: countries, isLoading: isLoadingCountries } = useGetCountries();
  const { data: states, isLoading: isLoadingStates } = useGetStates(
    countryCode || ""
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    register,
  } = useForm<CompleteProfileFormData>({
    resolver: yupResolver(completeProfileSchema),
    defaultValues: {
      first_name: currentProfile.first_name || "",
      last_name: currentProfile.last_name || "",
      phone: currentProfile.phone || "",
      country: currentProfile.country || "",
      state: currentProfile.state || "",
      city: currentProfile.city || "",
      zip_code: currentProfile.zip_code || "",
      address: currentProfile.address || "",
      address_two: currentProfile.address_two || "",
    },
  });

  useEffect(() => {
    const subscription = watch((value) => {
      if (value.country) {
        const code =
          COUNTRY_CODE_MAP[
            value.country as keyof typeof COUNTRY_CODE_MAP
          ];
        if (code) {
          setCountryCode(code);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const countryOptions = countries?.map((country) => ({
    value: country.name,
    label: country.name,
    code: country.code2,
  }));

  const statesOptions = states?.map((state) => ({
    value: state.name,
    label: state.name,
  }));

  const onSubmit: SubmitHandler<CompleteProfileFormData> = (data) => {
    updateProfile(
      {
        first_name: data.first_name,
        last_name: data.last_name,
        phone: data.phone,
        email: currentProfile.email,
        country: data.country,
        state: data.state,
        city: data.city,
        zip_code: data.zip_code,
        address: data.address,
        address_two: data.address_two,
      },
      {
        onSuccess: () => {
          setNotification(
            "success",
            "Profile completed successfully!",
            "complete_profile"
          );
          onSuccess();
        },
        onError: (error) => {
          const { addApiErrorNotification } = useNotificationStore.getState();
          addApiErrorNotification(error, "complete_profile", "complete_profile");
        },
      }
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="relative w-full max-w-[640px] bg-white rounded-[10px] py-[69px] px-[60px]">
        {/* Logo */}
        <div className="flex justify-start mb-[78px]">
          <div className="w-[360px] h-[76px]">
            <TeelogixLogo />
          </div>
        </div>

        {/* Heading */}
        <h1 className="font-['Aleo'] text-[40px] leading-[1.2] font-extrabold text-[#171d3d] mb-[87px]">
          Complete Your Profile
        </h1>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-[24px]">
          {/* First and Last Name Row */}
          <div className="grid grid-cols-2 gap-[25px]">
            {/* First Name */}
            <div className="flex flex-col gap-[12px]">
              <label className="font-['Poppins'] text-[14px] font-medium leading-[1.2] text-[#6b855c] uppercase text-left">
                FIRST NAME*
              </label>
              <input
                type="text"
                placeholder="Enter..."
                autoComplete="given-name"
                {...register("first_name")}
                className="h-[44px] px-4 py-[11px] border border-[#9ca2b8] rounded-[6px] font-['Poppins'] text-[16px] font-normal leading-[1.4] text-[#3a4259] placeholder:text-[#3a4259] focus:outline-none focus:border-green2 focus:ring-1 focus:ring-green2"
              />
              {errors.first_name && (
                <span className="text-xs text-red-500 -mt-2">
                  {errors.first_name.message}
                </span>
              )}
            </div>

            {/* Last Name */}
            <div className="flex flex-col gap-[12px]">
              <label className="font-['Poppins'] text-[14px] font-medium leading-[1.2] text-[#6b855c] uppercase text-left">
                LAST NAME*
              </label>
              <input
                type="text"
                placeholder="Enter..."
                autoComplete="family-name"
                {...register("last_name")}
                className="h-[44px] px-4 py-[11px] border border-[#9ca2b8] rounded-[6px] font-['Poppins'] text-[16px] font-normal leading-[1.4] text-[#3a4259] placeholder:text-[#3a4259] focus:outline-none focus:border-green2 focus:ring-1 focus:ring-green2"
              />
              {errors.last_name && (
                <span className="text-xs text-red-500 -mt-2">
                  {errors.last_name.message}
                </span>
              )}
            </div>
          </div>

          {/* Phone Number */}
          <div className="flex flex-col gap-[12px]">
            <PhoneInput
              name="phone"
              control={control}
              errors={errors}
              label="PHONE NUMBER*"
              placeholder="Phone number..."
              labelClassName="font-['Poppins'] text-[14px] font-medium leading-[1.2] text-[#6b855c] uppercase text-left"
              className="w-[255px]"
              defaultCountry="US"
            />
          </div>

          {/* Address Section */}
          <div className="space-y-[13px] pt-[23px]">
            <label className="font-['Poppins'] text-[14px] font-medium leading-[1.2] text-[#6b855c] uppercase block text-left">
              ADDRESS (OPTIONAL)
            </label>

            {/* Country, State, City Row */}
            <div className="grid grid-cols-[149px_126px_1fr] gap-[9px]">
              {/* Country */}
              <Controller
                name="country"
                control={control}
                render={({ field }) => (
                  <div className="relative">
                    <select
                      {...field}
                      disabled={isLoadingCountries}
                      onChange={(e) => {
                        field.onChange(e);
                        const selectedCountry = countryOptions?.find(
                          (c) => c.value === e.target.value
                        );
                        if (selectedCountry?.code) {
                          setCountryCode(selectedCountry.code);
                        }
                      }}
                      className="w-full h-[44px] px-4 py-[7px] border border-[#9ca2b8] rounded-[6px] font-['Poppins'] text-[16px] font-normal leading-[1.4] text-[#3a4259] bg-white focus:outline-none focus:border-green2 focus:ring-1 focus:ring-green2 appearance-none cursor-pointer disabled:bg-gray7 disabled:cursor-not-allowed"
                    >
                      <option value="">Country...</option>
                      {countryOptions?.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-6 h-6 text-[#3a4259] pointer-events-none" />
                  </div>
                )}
              />

              {/* State */}
              <Controller
                name="state"
                control={control}
                render={({ field }) => (
                  <div className="relative">
                    <select
                      {...field}
                      disabled={isLoadingStates || !countryCode}
                      className="w-full h-[44px] px-4 py-[7px] border border-[#9ca2b8] rounded-[6px] font-['Poppins'] text-[16px] font-normal leading-[1.4] text-[#3a4259] bg-white focus:outline-none focus:border-green2 focus:ring-1 focus:ring-green2 appearance-none cursor-pointer disabled:bg-gray7 disabled:cursor-not-allowed"
                    >
                      <option value="">State...</option>
                      {statesOptions?.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-6 h-6 text-[#3a4259] pointer-events-none" />
                  </div>
                )}
              />

              {/* City */}
              <Controller
                name="city"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="text"
                    placeholder="City..."
                    autoComplete="address-level2"
                    className="h-[44px] px-4 py-[7px] border border-[#9ca2b8] rounded-[6px] font-['Poppins'] text-[16px] font-normal leading-[1.4] text-[#3a4259] placeholder:text-[#3a4259] focus:outline-none focus:border-green2 focus:ring-1 focus:ring-green2"
                  />
                )}
              />
            </div>

            {/* Address Line 1 */}
            <input
              type="text"
              placeholder="Address line 1..."
              autoComplete="address-line1"
              {...register("address")}
              className="w-full h-[44px] px-4 py-[11px] border border-[#9ca2b8] rounded-[6px] font-['Poppins'] text-[16px] font-normal leading-[1.4] text-[#3a4259] placeholder:text-[#3a4259] focus:outline-none focus:border-green2 focus:ring-1 focus:ring-green2"
            />

            {/* Address Line 2 */}
            <input
              type="text"
              placeholder="Address line 2.."
              autoComplete="address-line2"
              {...register("address_two")}
              className="w-full h-[44px] px-4 py-[11px] border border-[#9ca2b8] rounded-[6px] font-['Poppins'] text-[16px] font-normal leading-[1.4] text-[#3a4259] placeholder:text-[#3a4259] focus:outline-none focus:border-green2 focus:ring-1 focus:ring-green2"
            />
          </div>

          {/* Buttons Column */}
          <div className="flex flex-col items-center gap-[16px] pt-[37px]">
            {/* Complete Sign Up Button */}
            <button
              type="submit"
              disabled={isPending}
              className="w-full h-[70px] bg-[#08712f] rounded-[10px] font-['Poppins'] text-[19px] font-semibold text-white hover:bg-green1 transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              {isPending ? "Saving..." : "Complete sign up"}
              {!isPending && <ArrowRight className="w-6 h-6" />}
            </button>

            {/* "or" text separator */}
            {onSignOut && (
              <p className="font-['Poppins'] text-[14px] font-normal text-[#3a4259]">
                or
              </p>
            )}

            {/* Sign Out Button */}
            {onSignOut && (
              <button
                type="button"
                onClick={onSignOut}
                className="w-full h-[70px] bg-[#dfe4ed] rounded-[10px] font-['Poppins'] text-[19px] font-semibold text-[#3a4259] hover:bg-gray5 transition-colors flex items-center justify-center"
              >
                Sign out
              </button>
            )}
          </div>
        </form>
      </div>

      <ToastNotification
        position="top-right"
        variant="green"
        source="complete_profile"
      />
    </div>
  );
};

export default CompleteProfileModal;
